<template>
  <div>
    <list-header title="Services" class="mb-4" :style="{position: 'relative'}">
      <icon-button
        :icon-class="'bi bi-three-dots'"
        @click="showGroupByModal"
        :btnStyle="{
          bottom: '12px',
          right: '17px',
          position: 'absolute',
          fontSize: '18px',
        }"
      />
    </list-header>

    <pull-to-refresh :refreshFunction="getServices">
      <!-- Services Found -->
      <div v-if="populatedServices && populatedServices.length > 0"> <!-- if populatedServices is not undefined(init value) and is not empty array(API return data) -->
        <div class="style-content">

          <nav-tab
            :tabs="tabList"
            :selected-tab="selectedTabID"
            @selected-tab="selectedTabID = $event"
            cssClass="scss-tabs"
          >
            <div v-for="service in populatedServices" :key="`sec-${groupBy === 'section' ? service.sectionID : service.categoryID}`">
              <tab :isSelected="selectedTabID === (groupBy === 'section' ? service.sectionID : service.categoryID)">
                <fieldset id="group1">
                  <div
                    v-for="data in service.services"
                    :key="`srv-${data.id}`"
                    @click="viewItemDetail(data)"
                  >
                    <div
                      class="scss-round-container d-flex flex-row mb-2 scss-click-effect"
                    >
                      <expandable-list
                        class="flex-grow-1"
                        :border="!!data.originalPrice">
                        <expandable-list-header
                          v-if="!!data.originalPrice"
                          :content="getPromotionHeaderAlt(data)"/>
                        <expandable-list-item
                          :imgUrl="data.imageURL"
                          :name="data.name"
                          :processingTime="formatProcessingTime(data.type, data.processingTime)"
                          :description="data.description"
                          :originalPrice="data.originalPrice"
                          :salePrice="data.salePrice"
                          :hidePrice="init.hideServicePrice"
                          :clickable="false"
                        >
                          <div class="d-flex flex-row align-items-center me-2">
                            <icon-container
                              :iconClass="'bi-plus-lg'"
                              :cssClass="`style-border ${data.futurePromotion ? 'scss-not-allowed' : ''}`"
                              @click.stop="goToMakeAppointment(data)"
                              />
                          </div>
                        </expandable-list-item>
                      </expandable-list>
                    </div>
                  </div>
                </fieldset>
              </tab>
            </div>
          </nav-tab>
        </div>
      </div>

      <!-- To avoid display loading spinner and showing `No more services found.` message -->
      <div v-else-if="loading">
        <item-not-found message="Loading services." />
      </div>

      <!-- Services not found -->
      <div v-else>
        <item-not-found message="No more services found." />
      </div>
    </pull-to-refresh>
    <product-listing-filter-modal
      :title="'Service Options'"
      :groupBy="groupBy"
      @update:groupBy="updateGroupBySelection"/>
  </div>
</template>

<script>
import {
  defineAsyncComponent,
  markRaw
} from "vue";
import {
  mapActions,
  mapGetters,
  mapMutations
} from "vuex";
import { getFormattedNumber } from "@/common/helpers/PriceHelper.js";
import {
  formatProcessingTime,
  getPromotionHeaderAlt
} from "@/common/helpers/ProductHelper";
import ListHeader from "@/components/ListHeader.vue";
import ExpandableList from "@/components/ExpandableList.vue";
import ExpandableListHeader from "@/components/ExpandableListHeader.vue";
import ExpandableListItem from "@/components/ExpandableListItem.vue";
import NavTab from "@/components/tab/NavTab.vue";
import IconButton from "@/components/button/IconButton.vue";
import IconContainer from "@/components/IconContainer.vue";
import ProductListingFilterModal, { Modal } from "../components/ProductListingFilterModal.vue";
import Tab from "@/components/tab/Tab.vue";
import ItemNotFound from "@/components/ItemNotFound.vue";
import PullToRefresh from "@/components/PullToRefresh";
import { openModal, closeModal } from "@/store/modal";
import { useToast } from "vue-toastification";

export default {
  name: "Service",
  components: {
    ListHeader,
    ExpandableList,
    ExpandableListHeader,
    ExpandableListItem,
    NavTab,
    IconButton,
    IconContainer,
    ProductListingFilterModal,
    Tab,
    ItemNotFound,
    PullToRefresh,
  },
  data() {
    return {
      groupBy: "section", // value is either section/category. default as section.
      selectedTabID: null,
      selectedServiceID: null,
    };
  },
  computed: {
    populatedServices() {
      // defined and populate the services sections or categories data(without service item)
      const list = this.services?.reduce((accumulated, service) => {
        const sectionId = service?.productSection?.id || 0;
        const categoryId = service?.productCategory?.id || 0;
        const exists = this.groupBy === "section" ?
            accumulated.find((data) => data.sectionID == sectionId) : // group by section
            accumulated.find((data) => data.categoryID == categoryId); // group by category

        if (exists === undefined) {
          // just push section and category data(id, name) even though one of them is wrong.
          // it's safe because UI is displaying data based group by value.
          accumulated.push({
            sectionID: sectionId || 0,
            categoryID: categoryId || 0,
            sectionName: service?.productSection?.name || 'Others',
            categoryName: service?.productCategory?.name || 'Others',
            services: [],
          });
        };

        return accumulated;
      }, []);

      // populate services sections or categories services items list
      if (list !== undefined && list.length > 0) {
        list.map((data) => {
          data.services = this.services?.
            filter((service) => {
              const sectionId = service?.productSection?.id || 0;
              const categoryId = service?.productCategory?.id || 0;

              return this.groupBy === "section" ?
                sectionId === data.sectionID : // group by section
                categoryId === data.categoryID; // group by category
            })
            .map((service) => {
              return {
                id: service.id,
                name: service.name,
                description: service.description,
                type: service.type,
                processingTime: service.service.processingTime,
                formatProcessingTime: service.service.formatProcessingTime,
                price: service.price,
                imageURL: service.image,
                sectionName: service?.productSection?.name || 'Others',
                categoryName: service?.productCategory?.name || 'Others',
                promotionId: service.service.promotionId,
                promotionPrice: service.service.promotionPrice,
                promotionStartDate: service.service.startDate,
                promotionEndDate: service.service.endDate,
                promotion: service.promotion,
                salePrice: service.salePrice,
                originalPrice: service.originalPrice,
                prepaidItemId: 0,
              };
            });
        });
      }

      return list;
    },
    tabList() {
      if (this.populatedServices) {
        return this.populatedServices
          .map((service) => {
            return this.groupBy === "section" ?
              // group by section
              {
                id: service.sectionID,
                name: `${service.sectionName} (${service.services.length})`,
              } :
              // group by category
              {
                id: service.categoryID,
                name: `${service.categoryName} (${service.services.length})`,
              };
          })
          .sort((a, b) => {
            // Make the others always at the end
            if (a.id === 0) {
              return 1;
            }
            if (b.id === 0) {
              return -1;
            }

            // Sort as normal
            return a.id > b.id ? 1 : -1;
          });
      }

      return [];
    },
    selectedServiceDetails() {
      return this.setSelectedService(this.selectedServiceID);
    },
    buttonEnabled() {
      return Boolean(this.selectedServiceID);
    },
    ...mapGetters({
      init: "init",
      services: "services",
      loading: "showLoading",
    }),
  },
  methods: {
    showGroupByModal() {
      var myModal = new Modal(document.getElementById("product-listing-filter-modal-id")); // use <product-listing-filter-modal /> default id
      myModal.show();
    },
    updateGroupBySelection(value) {
      this.groupBy = value;
      this.selectedTabID = null; // reset
    },
    async fetchServices() {
      // If services already fetched
      if (this.services !== null) {
        return;
      }

      await this.getServices();
    },
    setSelectedService(id) {
      let serviceList = this.populatedServices
        .map((service) => service.services)
        .flat(1);

      return serviceList.find((service) => service.id === parseInt(id));
    },
    goToMakeAppointment(service) {
      sessionStorage.setItem("serviceDetails", JSON.stringify(service));

      this.$router.push("/make-appointment");
    },
    viewItemDetail(service) {
      openModal(
        {
          id: "expandableListItemDetailModal",
        },
        markRaw(defineAsyncComponent(() => import("@/components/ExpandableListItemDetail.vue"))),
        {
          image: service.imageURL,
          processingTime: service.formatProcessingTime,
          name: service.name,
          description: service.description,
          originalPrice: service.futurePromotion ? service.futureOriginalPrice : service.originalPrice,
          salePrice: service.futurePromotion ? service.futureSalePrice : service.salePrice,
          section: service.sectionName,
          category: service.categoryName,
          promotion: service.promotion,
          futurePromotion: service.futurePromotion,
          hidePrice: this.init.hideServicePrice,
        },
        {},
        [
          {
            text: 'Book',
            class: 'scss-blue-button',
            clickFn: () => {
              if (!service.futurePromotion) {
                this.goToMakeAppointment(service);
                closeModal();
              } else {
                useToast().warning("Promotion not started.");
              }
            }
          },
          {
            text: 'Close',
            clickFn: () => {
              closeModal();
            }
          }
        ]
      );
    },
    formatProcessingTime,
    getPromotionHeaderAlt,
    getFormattedNumber,
    ...mapActions(["getServices"]),
    ...mapMutations(["showLoading"]),
  },
  watch: {
    populatedServices: {
      handler(newValue) {
        if (newValue !== undefined) {
          this.showLoading(false);
        } else {
          this.showLoading(true);
        }
      },
      immediate: true
    },
  },
  created() {
    this.fetchServices();
  },
};
</script>

<style lang="scss" scoped>
.style-checkbox {
  font-size: 20px;
}

.style-content {
  width: 90%;
  margin: 0 auto 0 auto;
  margin-bottom: 70px;
}
</style>
